import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { DynamicForm } from 'src/components';
import { getDynamicFormDefaultValues } from 'src/components/Forms/DynamicForm/dynamic-form-default-values';
import { ModalContainer } from 'src/components/ModalContainer';
import { useUserGroupOptions } from 'src/graphql';
import {
  AttributeDefinition,
  getFilters,
  useCustomAttributeDefinition,
} from 'src/graphql/entities/custom-attributes/custom-attributes';
import { parseOptionsInput } from 'src/graphql/entities/custom-attributes/parse-options-input';
import { useGlobalStore } from 'src/store';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { useNotifications } from 'src/utils/hooks';
import { parseJsonSafely } from 'src/utils/objects';
import { slugify } from 'src/utils/strings';
import {
  CustomAttributeFormValues,
  useCustomAttributeForm,
} from './use-custom-attribute-form';

export const AddCustomAttributeModal = () => {
  const { closeModal } = useModalMethods(Modals.AddCustomAttributeModal);
  const { isOpened, modalProps } = useModalState(Modals.AddCustomAttributeModal);

  const { customAttribute, entity, customAttributeDefinition } = modalProps ?? {};

  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();

  const filters = useMemo(
    () => getFilters({ partnerId: selectedPartnerId }),
    [selectedPartnerId],
  );

  const { saveCustomAttributeDefinition } = useCustomAttributeDefinition({
    filters,
  });

  const { showSuccessAlert, showErrorAlert } = useNotifications();

  const { userGroupOptions } = useUserGroupOptions();

  const formValues = useMemo(() => {
    if (!customAttribute) {
      return {
        restrictedToUserGroups: [] as {
          id: string;
          label: string;
        }[],
      } as CustomAttributeFormValues;
    }

    return {
      ...customAttribute,
      options: customAttribute.options?.map((option) => option.value).join('\n'),
      restrictAccess: !customAttribute.visibility.visibleToAllUsers,
      group: customAttribute.group
        ? { id: customAttribute.group, label: customAttribute.group }
        : undefined,
      restrictedToUserGroups:
        customAttribute.visibility.restrictedToUserGroups?.filter(Boolean).map(
          (id) =>
            userGroupOptions.find((userGroup) => userGroup.id === id) ?? {
              id,
              label: id,
            },
        ) ?? [],
    } as CustomAttributeFormValues;
  }, [customAttribute, userGroupOptions]);

  const { attributeFormFields } = useCustomAttributeForm(
    customAttributeDefinition,
    formValues,
  );

  const formMethods = useForm<CustomAttributeFormValues>({
    defaultValues: getDynamicFormDefaultValues(attributeFormFields, formValues),
    mode: 'onChange',
  });
  const { formState, handleSubmit } = formMethods;

  const handleAddCustomAttribute = useCallback(
    async (values: CustomAttributeFormValues) => {
      let definition = {
        attributes: {},
      } as AttributeDefinition;

      if (customAttributeDefinition?.definition) {
        definition =
          parseJsonSafely<AttributeDefinition>(customAttributeDefinition.definition) ||
          definition;
      }

      const isAttributeDuplicated =
        values.displayName &&
        definition?.attributes?.[values.displayName] &&
        customAttribute?.displayName !== values.displayName &&
        !definition.attributes[values.displayName].deleted;

      if (isAttributeDuplicated) {
        showErrorAlert(
          `Unable to ${customAttribute ? 'update' : 'create'} property. Property ${values.displayName} is already being used.`,
        );

        return;
      }

      if (!values.displayName) {
        return;
      }

      const restrictAccess = values.restrictAccess ?? false;

      const existingKey = customAttribute?.displayName
        ? definition.attributes[customAttribute.displayName]?.key
        : undefined;

      definition.attributes[values.displayName] = {
        ...(existingKey ? { key: existingKey } : {}),
        displayName: values.displayName,
        visibility: {
          visibleToAllUsers: !restrictAccess,
          restrictedToUserGroups: restrictAccess
            ? (values.restrictedToUserGroups?.map((group) => group.id) ?? [])
            : [],
        },
        defaultValue: values.defaultValue,
        type: values.type?.id!,
        deleted: false,
      };

      const parsedOptions = parseOptionsInput(values.options);

      if (parsedOptions.length > 0) {
        definition.attributes[values.displayName].options = parsedOptions.map((value) => ({
          key: slugify(value),
          value,
        }));
      }

      if (values.relatedEntity) {
        definition.attributes[values.displayName].relatedEntity = values.relatedEntity.id;
      }

      if (values.group) {
        definition.attributes[values.displayName].group = values.group.id;
      }

      if (
        customAttribute?.displayName &&
        values.displayName !== customAttribute?.displayName
      ) {
        delete definition.attributes[customAttribute.displayName];
      }

      closeModal();

      const response = await saveCustomAttributeDefinition({
        customAttributeDefinition,
        definition: JSON.stringify(definition),
        entity,
        partnerId: selectedPartnerId!,
      });

      if (!response) {
        return;
      }

      showSuccessAlert(customAttribute ? 'Property was updated' : 'Property was added');
    },
    [
      customAttributeDefinition,
      customAttribute,
      closeModal,
      saveCustomAttributeDefinition,
      entity,
      selectedPartnerId,
      showSuccessAlert,
      showErrorAlert,
    ],
  );

  return (
    <ModalContainer
      open={isOpened}
      size="medium"
      title={customAttribute ? 'Update Property' : 'Add Property'}
      actions={[
        { label: 'Cancel', kind: 'secondary', testId: 'add-custom-attribute-cancel' },
        {
          label: customAttribute ? 'Save property' : 'Add property',
          kind: 'primary',
          onClick: handleSubmit(handleAddCustomAttribute),
          disabled: !formState.isValid,
          testId: 'add-custom-attribute-submit',
        },
      ]}
      onClose={closeModal}
      data-testid="add-custom-attribute-modal"
    >
      <DynamicForm fields={attributeFormFields} formMethods={formMethods} sx={{ gap: 2 }} />
    </ModalContainer>
  );
};
